import { useAuth } from "@/contexts/AuthContext";
import {
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
} from "@/components/common/surfaceClasses";
import { canEditAdsGuidance } from "@/lib/adsAssistantPermissions";
import { AdsLibraryAiHelper } from "@/components/ads-assistant/AdsLibraryAiHelper";
import { AdsAssistantSocialMappingsSection } from "@/components/ads-assistant/AdsAssistantSocialMappingsSection";
import { AdsLibraryCategoriesPanel } from "@/components/ads-assistant/AdsLibraryCategoriesPanel";
import { AdsLibraryPoliciesPanel } from "@/components/ads-assistant/AdsLibraryPoliciesPanel";
import { AdsLibraryPostTagsPanel } from "@/components/ads-assistant/AdsLibraryPostTagsPanel";
import { AdsLibraryTemplatesPanel } from "@/components/ads-assistant/AdsLibraryTemplatesPanel";
import type { LibrarySectionKey } from "@/components/ads-assistant/adsLibrarySectionShared";
import { useAdsAssistantLibrarySection } from "@/components/ads-assistant/useAdsAssistantLibrarySection";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  scopedOrgId: string | null;
  embedded?: boolean;
};

/**
 * Taxonomy, templates, and policy/guideline storage. Editing is role-gated (see adsAssistantPermissions); all Ads roles can read.
 */
export function AdsAssistantLibrarySection({ scopedOrgId, embedded }: Props) {
  const auth = useAuth();
  const canEdit = canEditAdsGuidance(auth);
  const lib = useAdsAssistantLibrarySection(scopedOrgId);

  if (!scopedOrgId) {
    return embedded ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">Choose an organization to load the library.</p>
    ) : (
      <section className={`${cardSurfaceClass} px-4 py-3`}>
        <h3 className={`text-sm font-semibold ${cardTitleTextClass}`}>Library — categories, templates, policies, post tags</h3>
        <p className={`mt-2 text-sm ${cardMutedTextClass}`}>Choose an organization to load the library.</p>
      </section>
    );
  }

  const editNote = !canEdit ? (
    <span className="text-amber-800 dark:text-amber-200/90">
      Editing requires admin, owner, marketing, trainer, or the add-on role &quot;Ads guidance editor&quot; (platform ops
      included).
    </span>
  ) : (
    <span className="text-emerald-800 dark:text-emerald-300/90">You can edit library items for this org.</span>
  );

  return (
    <section
      className={embedded ? "space-y-5" : `${cardSurfaceClass} px-4 py-3 space-y-5`}
      aria-labelledby={embedded ? undefined : "ads-library-heading"}
    >
      <details className="rounded-xl border border-sky-100 bg-sky-50/60 dark:border-sky-900/40 dark:bg-sky-950/25">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-sky-950 select-none [&::-webkit-details-marker]:hidden dark:text-sky-100">
          <span>Start here</span>
          <span className="text-[11px] text-sky-700 dark:text-sky-300">▾</span>
        </summary>
        <ul className="border-t border-sky-100 mt-0 list-disc space-y-1 px-6 py-3 text-xs text-sky-900/90 dark:border-sky-900/40 dark:text-sky-100/85 leading-relaxed">
          <li>
            <strong>Categories</strong> — group posts (e.g. promos vs community). Reps pick a category when they log an ad.
          </li>
          <li>
            <strong>Templates</strong> — named formats (lot shot, testimonial, event). Shows as a badge on each indexed post.
          </li>
          <li>
            <strong>Policies</strong> — plain-language rules plus short text snippets the system scans for in post copy.
          </li>
          <li>
            <strong>Post tags</strong> — trainer-defined labels for indexing (chips on posts, filters in the posts index).
          </li>
        </ul>
      </details>

      <div className="flex flex-wrap items-start justify-between gap-2">
        {embedded ? (
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-xs text-gray-600 dark:text-gray-400">{editNote}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 max-w-prose leading-relaxed">
              Categories = chips on logged posts; templates = named formats (badge in the index); policies = stub compliance
              hints; post tags = trainer vocabulary for ingest and index filters. Archetype tags today are heuristic, not full
              LLM classification.
            </p>
          </div>
        ) : (
          <div>
            <h3 id="ads-library-heading" className={`text-sm font-semibold ${cardTitleTextClass}`}>
              Library — categories, templates, policies, post tags
            </h3>
            <p className={`mt-1 text-xs max-w-prose ${cardMutedTextClass}`}>
              Define how posts are classified, reusable creative shells for the field, guideline text plus substring patterns the
              stub analyzer uses for compliance hints, and optional post tags for indexing. {editNote}
            </p>
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 max-w-prose leading-relaxed">
              <strong className="font-medium text-gray-800 dark:text-gray-200">How it fits together:</strong> categories are
              the chips reps choose when logging a post; templates are the named “formats” that show as a recognition badge on
              each indexed row; policies add substring warnings the stub pass surfaces next to the post. Full LLM classification
              of creative archetypes stays on the roadmap—today’s tags are fast heuristics only.
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={() => void lib.load()}
          disabled={lib.loading}
          className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100 dark:hover:bg-zinc-700"
        >
          {lib.loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {lib.banner ? (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-sm text-emerald-950">{lib.banner}</p>
      ) : null}
      {lib.error ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-950">{lib.error}</p>
      ) : null}

      <div className="space-y-4">
        <div
          {...{ [FIREFLY_TARGET_ATTR]: "ads-guidance-library" }}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {(["categories", "templates", "policies", "post_tags"] as LibrarySectionKey[]).map((key) => (
            <button
              key={key}
              type="button"
              {...(key === "policies"
                ? { [FIREFLY_TARGET_ATTR]: "ads-guidance-publish-or-policies" }
                : {})}
              onClick={() => lib.setExpandedSection((prev) => (prev === key ? null : key))}
              className={
                "rounded-xl border bg-white p-3 text-left transition-colors hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 " +
                (lib.expandedSection === key
                  ? "border-blue-300 shadow-[0_0_0_1px_rgba(59,130,246,0.2)] dark:border-blue-700"
                  : "border-gray-200 dark:border-gray-600")
              }
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {key === "categories"
                    ? "Categories"
                    : key === "templates"
                      ? "Templates"
                      : key === "policies"
                        ? "Policies"
                        : "Post tags"}
                </span>
                <span className="text-xs text-gray-500">{lib.expandedSection === key ? "▾" : "▸"}</span>
              </div>
            </button>
          ))}
        </div>

        {lib.expandedSection === "categories" ? (
          <AdsLibraryCategoriesPanel
            canEdit={canEdit}
            categories={lib.categories}
            catSlug={lib.catSlug}
            setCatSlug={lib.setCatSlug}
            catLabel={lib.catLabel}
            setCatLabel={lib.setCatLabel}
            setError={lib.setError}
            setBanner={lib.setBanner}
            load={lib.load}
          />
        ) : null}

        {lib.expandedSection === "templates" ? (
          <AdsLibraryTemplatesPanel
            canEdit={canEdit}
            scopedOrgId={scopedOrgId}
            categories={lib.categories}
            templates={lib.templates}
            postTagDefs={lib.postTagDefs}
            editor={lib.editor}
            setError={lib.setError}
            setBanner={lib.setBanner}
            load={lib.load}
          />
        ) : null}

        {lib.expandedSection === "policies" ? (
          <AdsLibraryPoliciesPanel
            canEdit={canEdit}
            policies={lib.policies}
            polTitle={lib.polTitle}
            setPolTitle={lib.setPolTitle}
            polBody={lib.polBody}
            setPolBody={lib.setPolBody}
            polPatterns={lib.polPatterns}
            setPolPatterns={lib.setPolPatterns}
            polSeverity={lib.polSeverity}
            setPolSeverity={lib.setPolSeverity}
            setError={lib.setError}
            setBanner={lib.setBanner}
            load={lib.load}
          />
        ) : null}

        {lib.expandedSection === "post_tags" ? (
          <AdsLibraryPostTagsPanel
            canEdit={canEdit}
            postTagDefs={lib.postTagDefs}
            postTagSlug={lib.postTagSlug}
            setPostTagSlug={lib.setPostTagSlug}
            postTagLabel={lib.postTagLabel}
            setPostTagLabel={lib.setPostTagLabel}
            postTagSortDraft={lib.postTagSortDraft}
            setPostTagSortDraft={lib.setPostTagSortDraft}
            postTagKind={lib.postTagKind}
            setPostTagKind={lib.setPostTagKind}
            setError={lib.setError}
            setBanner={lib.setBanner}
            load={lib.load}
          />
        ) : null}
      </div>

      {canEdit ? (
        <details className="rounded-xl border border-violet-200/90 bg-white dark:border-violet-900/60 dark:bg-zinc-900">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-violet-950 select-none [&::-webkit-details-marker]:hidden dark:text-violet-100">
            <span>AI drafting help</span>
            <span className="text-xs text-violet-700 dark:text-violet-300">▸</span>
          </summary>
          <div className="border-t border-violet-100 p-3 dark:border-violet-900/40">
            <AdsLibraryAiHelper
              scopedOrgId={scopedOrgId}
              postTagDefs={lib.postTagDefs}
              onApplyDraft={lib.applyAiDraft}
              formHasContent={lib.formHasContent}
              applyBankTemplateDraft={lib.applyBankTemplateDraft}
              applyBankCategoryDraft={lib.applyBankCategoryDraft}
            />
          </div>
        </details>
      ) : null}

      <AdsAssistantSocialMappingsSection scopedOrgId={scopedOrgId} />
    </section>
  );
}
